// @flow

import { v4 } from 'uuid';
import queryString from 'query-string';
import React, { Fragment } from 'react';
import type { Node } from 'react';
import { useSelector } from 'react-redux';
import { store as STORE_CONFIG } from 'Config';
import { artist as ARTIST_ENUM } from 'Enum';
import { useQuery } from 'shared_services/apollo/useQuery';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { selectByStoreKey } from 'shared_services/redux/selectors/byKey';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { convertPayloadToQueryVars } from 'shared_services/riseart/utils/Utils';
import FILTER_ARTIST_ART_QUERY from 'shared_data/queries/artist/filterArt.graphql';

type Props = {
  artistId: number,
  ssr: boolean,
  children: Function,
  location: Object,
  itemsPerPage: number,
  onCompleted?: Function,
  onError?: Function,
};

const ITEMS_PER_PAGE = 16;
const PRELOADER_SUBSCRIBER_IDS = v4();

/**
 * ArtistArtList
 */
export const ArtistArtList = ({
  artistId,
  ssr,
  children,
  location,
  itemsPerPage = ITEMS_PER_PAGE,
  onCompleted,
  onError,
}: Props): Node => {
  const storeCode = useStoreCode();
  const { filters } = useSelector(selectByStoreKey(STORE_CONFIG.keys.filterArtistArt)) || {};
  const { page, sort } = queryString.parse(location.search) || {};
  const parsedPage = parseInt(page, 10);
  const requestedPage = parsedPage > 0 ? parsedPage : 1;
  const requestedSort = Object.keys(ARTIST_ENUM.art.rank).some(
    (key) => ARTIST_ENUM.art.rank[key] === sort,
  )
    ? sort
    : ARTIST_ENUM.art.rank.RANKING_ARTIST;
  const { loading, data } = useQuery(FILTER_ARTIST_ART_QUERY, {
    ssr,
    skip: !storeCode || !artistId || !filters,
    variables: {
      artistId,
      inputArtFilter: convertPayloadToQueryVars(
        {
          items: itemsPerPage,
          page: requestedPage,
          sort: requestedSort,
          ...filters,
        },
        storeCode,
      ),
      ignoreArtIds: '0',
    },
    ...(typeof onCompleted === 'function' ? { onCompleted } : null),
    ...(typeof onError === 'function' ? { onError } : null),
  });

  if (!filters) {
    return null;
  }

  const { items = [], pagination = {} } = (data && data.filterArtistArt) || {};

  return (
    <Fragment>
      <DataProviderPreloaderPage uniqueId={PRELOADER_SUBSCRIBER_IDS} attach={loading} />
      {children({ loading, items, pagination, filters })}
    </Fragment>
  );
};
